import { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Box,
    Divider,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HSTable from "../global/Table";
import { getAllPhcMaterials } from "../../services/dearators_phc/DeaeratorPhcMaterialService";

const CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: 3,
    boxShadow: 3,
    overflow: "hidden",
    position: "relative",
    marginTop: "20px",
};

const CLOSE_BUTTON_SX = {
    position: "absolute",
    top: 8,
    right: 8,
    color: "grey.600",
    zIndex: 1,
};

const IMAGE_SX = {
    width: "20%",
    objectFit: "contain",
    margin: "2% auto",
    display: { xs: "none", md: "block" },
};

const SECTION_BOX_SX = {
    borderRadius: 2,
    p: 2,
};

const ITEM_BOX_SX = {
    display: "flex",
    justifyContent: "space-between",
    mb: 1,
};


const formatValue = (val) => (val === undefined || val === null || val === "" ? "-" : val);

const formatPrice = (value) => {
    if (value == null || value === "") return "-";
    const number = Number(value);
    if (isNaN(number)) return "-";
    return number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const getTechnicalData = (deaerator, formatValue) => ({
    reservoir: {
        virola: [
            { label: "Diâmetro Ø", value: `${deaerator.reservoir.shell.diameter}` },
            { label: "Espessura", value: `${deaerator.reservoir.shell.thickness}` },
            { label: "Comprimento", value: `${deaerator.reservoir.shell.length}` },
            { label: "Material", value: `${deaerator.reservoir.shell.material}` },
        ],
        dishedEnd: [
            { label: "Diâmetro Ø", value: `${deaerator.reservoir.dishedEnd.diameter}` },
            { label: "Espessura", value: `${deaerator.reservoir.dishedEnd.thickness}` },
            { label: "Material", value: `${deaerator.reservoir.dishedEnd.material}` },
        ],
    },
    tower: {
        virola: [
            { label: "Diâmetro Ø", value: `${deaerator.tower.shell.diameter}` },
            { label: "Espessura", value: `${deaerator.tower.shell.thickness}` },
            { label: "Comprimento", value: `${deaerator.tower.shell.length}` },
            { label: "Material", value: `${deaerator.tower.shell.material}` },
        ],
        dishedEnd: [
            { label: "Diâmetro Ø", value: `${deaerator.tower.dishedEnd.diameter}` },
            { label: "Espessura", value: `${deaerator.tower.dishedEnd.thickness}` },
            { label: "Material", value: `${deaerator.tower.dishedEnd.material}` },
        ],
    },
    connection: [
        { label: "Diâmetro Ø", value: `${deaerator.connection.diameter}` },
        { label: "Material", value: `${deaerator.connection.material}` },
        { label: "Flanges", value: `${deaerator.connection.flanges}` },
    ],
    conditions: [
        { label: "Pressão", value: formatValue(deaerator.conditions?.service) },
        { label: "Stamp", value: formatValue(deaerator.conditions?.stamp) },
        { label: "Teste de Pressão", value: formatValue(deaerator.conditions?.testPressure) },
    ],
    capacity: [
        { label: "Capacidade", value: formatValue(deaerator.conditions?.capacity) + " t/h" },
    ],
});



export default function DeaeratorPhcDetail({ deaerator, onClose }) {

    const getProcessPrefix = (contract) => {
        return contract.startsWith("19") ? "subProcess:" : "process:";
    }

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchString, setSearchString] = useState(`${getProcessPrefix(deaerator.contract)}${deaerator.contract}&&`);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalElements, setTotalElements] = useState(0);

    useEffect(() => {
        setSearchString(`${getProcessPrefix(deaerator.contract)}${deaerator.contract}&&`);
        setPage(0);
    }, [deaerator]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const res = await getAllPhcMaterials(page, rowsPerPage, searchString);
                if (!cancelled) {
                    setData(res.content);
                    setTotalElements(res.totalElements);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [page, rowsPerPage, searchString]);

    const columns = [
        { field: "reference", label: "Referência", sortable: true },
        { field: "description", label: "Descrição", sortable: true },
        { field: "unit", label: "Unidade", sortable: true },
        { field: "quantity", label: "Quantidade", sortable: true },
        { field: "unitCost", label: "Preço Unitário", sortable: true, render: formatPrice },
        { field: "totalCost", label: "Preço Total", sortable: true, render: formatPrice },
        { field: "rubric", label: "Rubrica", sortable: true },
        { field: "lastCostPrice", label: "Último Preço de Custo", sortable: true },
    ];
    const enableSearch = false;

    const onSearchChange = (search) => setSearchString(search);

    const technicalData = getTechnicalData(deaerator, formatValue);

    const sections = [
        {
            title: "Reservatório",
            subsections: [
                { title: "Virola", items: technicalData.reservoir.virola },
                { title: "Fundo Copado", items: technicalData.reservoir.dishedEnd },
            ],
        },
        {
            title: "Torre",
            subsections: [
                { title: "Virola", items: technicalData.tower.virola },
                { title: "Fundo Copado", items: technicalData.tower.dishedEnd },
            ],
        },
        { title: "Ligação", items: technicalData.connection },
        {
            title: "Condições",
            subsections: [
                { title: "Pressão", items: technicalData.conditions },
                { title: "Capacidade", items: technicalData.capacity },
            ],
        },
    ];

    return (
        <Card sx={CARD_SX}>
            {onClose && (
                <IconButton onClick={onClose} sx={CLOSE_BUTTON_SX}>
                    <CloseIcon />
                </IconButton>
            )}

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
                <CardMedia
                    component="img"
                    sx={IMAGE_SX}
                    image={deaerator?.image ?? "https://via.placeholder.com/250"}
                    alt={deaerator?.name ?? "Deaerator"}
                />

                <CardContent sx={{ flex: 2, p: 3 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {deaerator?.name ?? `Deaerator - ${deaerator.client}`}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" paragraph>
                        {deaerator?.description ?? "Características do Desgasificador"}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
                            gap: 3,
                        }}
                    >
                        {sections.map((section, idx) => (
                            <Box key={idx} sx={SECTION_BOX_SX}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    gutterBottom
                                    align="center"
                                    sx={{ mb: "10px" }}
                                >
                                    {section.title}
                                </Typography>

                                {section.subsections
                                    ? section.subsections.map((sub, sIdx) => (
                                        <Box key={sIdx} sx={{ mb: 2 }}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                {sub.title}
                                            </Typography>
                                            {sub.items.map((item, iIdx) => (
                                                <Box key={iIdx} sx={ITEM_BOX_SX}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.label}
                                                    </Typography>
                                                    <Typography variant="body2">{item.value}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ))
                                    : section.items.map((item, iIdx) => (
                                        <Box key={iIdx} sx={{ ...ITEM_BOX_SX, mt: iIdx === 0 ? "40px" : "0px" }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.label}
                                            </Typography>
                                            <Typography variant="body2">{item.value}</Typography>
                                        </Box>
                                    ))}
                            </Box>
                        ))}
                    </Box>

                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ mt: 2, mb: 2 }}
                    >
                        Materiais
                    </Typography>

                    <HSTable
                        columns={columns}
                        data={data}
                        loading={loading}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        enableSearch={enableSearch}
                        totalCount={totalElements}
                        onRowsPerPageChange={(newRowsPerPage) => {
                            setRowsPerPage(newRowsPerPage);
                            setPage(0);
                        }}
                        onPageChange={setPage}
                        onSearchChange={onSearchChange}
                    />
                </CardContent>
            </Box>
        </Card>
    );
}
